"use client";

import { useEffect, useRef } from "react";
import styles from "../member.module.scss";
import type { Member } from "../_data/members";
import Avatar from "./Avatar";
import SocialLinks from "./SocialLinks";

type MemberPanelProps = {
  member: Member | null;
  onClose: () => void;
};

export default function MemberPanel({ member, onClose }: MemberPanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const open = member !== null;

  // Escで閉じる
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // 背面のスクロールを止める。サイドバーの mobile-menu-open と同じ body クラス方式。
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("member-panel-open");
    return () => document.body.classList.remove("member-panel-open");
  }, [open]);

  // 開いたら閉じるボタンへフォーカスを移し、閉じたら呼び出し元のカードへ戻す
  useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => trigger?.focus?.();
  }, [open]);

  // パネル内でフォーカスを循環させる（背面のカードへ抜けさせない）
  const onKeyDownTrap = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab" || !panelRef.current) return;
    const focusables = panelRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <div
        className={`${styles.overlay} ${open ? styles.overlayOpen : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        className={`${styles.panel} ${open ? styles.panelOpen : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="member-panel-name"
        aria-hidden={!open}
        onKeyDown={onKeyDownTrap}
      >
        <button
          ref={closeRef}
          type="button"
          className={styles.panelClose}
          onClick={onClose}
          aria-label="閉じる"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
            />
          </svg>
        </button>

        {member && (
          <div className={styles.panelInner}>
            <div className={styles.panelPhoto}>
              <Avatar member={member} variant="panel" />
            </div>

            <div className={styles.panelText}>
              <span className={styles.panelRole}>{member.role}</span>
              <h3 id="member-panel-name" className={styles.panelName}>
                {member.name}
              </h3>

              {member.career && (
                <div className={styles.careerText}>
                  {member.career.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                </div>
              )}

              <SocialLinks socials={member.socials} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
